import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Chip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import AuthPageFrame from '../components/AuthPageFrame';
import AuthLegalNotice from '../components/AuthLegalNotice';

interface SchoolOption {
  _id: string;
  name: string;
  email: string;
  address?: string;
  city?: string;
  country?: string;
}

const HighSchoolSignup: React.FC = () => {
  const defaultJambSubjects = [
    'Use of English', 'Mathematics', 'Physics', 'Chemistry', 'Biology',
    'Economics', 'Principles of Accounts', 'Commerce', 'Agricultural Science', 'Geography',
    'History', 'Government', 'Civics', 'Literature in English',
    'Physical and Health Education', 'Home Economics', 'Fine Art',
    'Agricultural Technology', 'Computer Studies',
    'Yoruba', 'Hausa', 'Igbo', 'Islamic Religious Studies',
    'Christian Religious Studies', 'French', 'Music',
    'Arabic',
  ];
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [age, setAge] = useState('');
  const [gender, setGender] = useState('');
  const [department, setDepartment] = useState('');
  const [jambSubjects, setJambSubjects] = useState<string[]>(['Use of English']);
  const [stateOfOrigin, setStateOfOrigin] = useState('');
  const [lastSchoolAttended, setLastSchoolAttended] = useState('');
  const [schoolOptions, setSchoolOptions] = useState<SchoolOption[]>([]);
  const [selectedSchool, setSelectedSchool] = useState<SchoolOption | null>(null);
  const [schoolInput, setSchoolInput] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [parentName, setParentName] = useState('');
  const [parentEmail, setParentEmail] = useState('');
  const [parentPhone, setParentPhone] = useState('');
  const [schoolSecretId, setSchoolSecretId] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingSchools, setLoadingSchools] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successOpen, setSuccessOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const loadSchools = async () => {
      setLoadingSchools(true);
      try {
        const response = await api.get('/schools');
        setSchoolOptions(response.data || []);
      } catch (err: any) {
        console.error('Failed to load schools', err);
        setError('Unable to load schools. Please try again later.');
      } finally {
        setLoadingSchools(false);
      }
    };

    loadSchools();
  }, []);

  const submit = async () => {
    setError(null);
    // Check if Not Applicable is selected
    const isNotApplicable = selectedSchool && selectedSchool._id === 'not_applicable';
    if (
      !name ||
      !email ||
      !password ||
      !age ||
      !gender ||
      !department ||
      !stateOfOrigin ||
      !lastSchoolAttended ||
      (!isNotApplicable && !selectedSchool) ||
      !address ||
      !city ||
      !country ||
      !parentName ||
      !parentEmail ||
      !parentPhone
    ) {
      setError('Please fill in all required fields, including school selection.');
      return;
    }
    if (jambSubjects.length !== 4) {
      setError('Select exactly three JAMB subjects in addition to Use of English.');
      return;
    }
    // If a school is selected (not Not Applicable), require schoolSecretId
    if (!isNotApplicable && !schoolSecretId) {
      setError('Please enter the School Secrete ID.');
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/register', {
        name,
        email,
        password,
        referralCode: referralCode.trim().toUpperCase(),
        age,
        gender,
        department,
        jambSubjects,
        stateOfOrigin,
        lastSchoolAttended,
        school: isNotApplicable ? 'not_applicable' : selectedSchool._id,
        schoolName: isNotApplicable ? 'Not Applicable' : selectedSchool.name,
        schoolEmail: isNotApplicable ? '' : selectedSchool.email,
        schoolSecretId: isNotApplicable ? '' : schoolSecretId,
        educationLevel: 'high-school',
        purpose: 'learning',
        address,
        city,
        country,
        parentName,
        parentEmail,
        parentPhone,
      });

      setLoading(false);
      setSuccessOpen(true);
    } catch (err: any) {
      setLoading(false);
      setError(err?.response?.data?.message || 'Registration failed');
    }
  };

  const handleSuccessOk = () => {
    setSuccessOpen(false);
    navigate('/high-school/login');
  };

  return (
    <>
      <AuthPageFrame mode="signup">
        <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
          High School Student Sign Up
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600, color: '#666' }}>
          Required Information
        </Typography>

        {/* Required Information */}
        <TextField label="Full name" fullWidth value={name} onChange={(e) => setName(e.target.value)} sx={{ mb: 2 }} required />
        <TextField label="Email" type="email" fullWidth value={email} onChange={(e) => setEmail(e.target.value)} sx={{ mb: 2 }} required />
        <TextField label="Password" type="password" fullWidth value={password} onChange={(e) => setPassword(e.target.value)} sx={{ mb: 3 }} required />
        <TextField
          label="Referral code (optional)"
          fullWidth
          value={referralCode}
          onChange={(event) => setReferralCode(event.target.value.slice(0, 8))}
          inputProps={{ maxLength: 8, autoCapitalize: 'characters' }}
          helperText="Enter an existing A3T code to support the person who referred you."
          sx={{ mb: 2 }}
        />

        {/* Personal Information */}
        <Typography variant="subtitle2" sx={{ mt: 3, mb: 1, fontWeight: 600, color: '#666' }}>Personal Information</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
          <TextField label="Age" type="number" fullWidth value={age} onChange={(e) => setAge(e.target.value)} required />
          <FormControl fullWidth required>
            <InputLabel>Gender</InputLabel>
            <Select value={gender} label="Gender" onChange={(e) => setGender(e.target.value)}>
              <MenuItem value="">Select gender</MenuItem>
              <MenuItem value="male">Male</MenuItem>
              <MenuItem value="female">Female</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <TextField label="State of Origin" fullWidth value={stateOfOrigin} onChange={(e) => setStateOfOrigin(e.target.value)} sx={{ mb: 2 }} required />
        <FormControl fullWidth sx={{ mb: 2 }} required>
          <InputLabel>Department</InputLabel>
          <Select
            value={department}
            label="Department"
            onChange={(e) => {
              setDepartment(e.target.value);
              setJambSubjects(['Use of English']);
            }}
          >
            <MenuItem value="">Select department</MenuItem>
            <MenuItem value="Science">Science</MenuItem>
            <MenuItem value="Commercial">Commercial</MenuItem>
            <MenuItem value="Art">Art</MenuItem>
          </Select>
        </FormControl>
        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600, color: '#666' }}>
          JAMB Subject Combination
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Use of English is compulsory. Select three other subjects ({jambSubjects.length - 1}/3).
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          This combination will be used for your JAMB Mock Test. The selected order will be used for your test sections.
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
          {jambSubjects.map((subject) => (
            <Chip key={subject} label={subject} color="primary" variant={subject === 'Use of English' ? 'filled' : 'outlined'} />
          ))}
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, mb: 2 }}>
          {defaultJambSubjects.filter((subject) => {
            if (subject === 'Use of English' || subject === 'Mathematics' || subject === 'Geography') return true;
            const scienceSubjects = ['Physics', 'Chemistry', 'Biology', 'Agricultural Science'];
            const commercialSubjects = ['Economics', 'Principles of Accounts', 'Commerce'];
            const artSubjects = ['History', 'Government', 'Civics', 'Literature in English', 'Arabic'];
            return department === 'Science' ? scienceSubjects.includes(subject)
              : department === 'Commercial' ? commercialSubjects.includes(subject)
              : department === 'Art' ? artSubjects.includes(subject)
              : false;
          }).map((subject) => {
            const checked = jambSubjects.includes(subject);
            const compulsory = subject === 'Use of English';
            return (
              <FormControlLabel
                key={subject}
                label={subject}
                control={(
                  <Checkbox
                    checked={checked}
                    disabled={compulsory || (!checked && jambSubjects.length === 4)}
                    onChange={() => setJambSubjects((current) => (
                      checked
                        ? current.filter((item) => item !== subject)
                        : [...current, subject]
                    ))}
                  />
                )}
              />
            );
          })}
        </Box>
        {jambSubjects.length !== 4 && (
          <Alert severity="info" sx={{ mb: 2 }}>Select exactly three JAMB subjects in addition to Use of English.</Alert>
        )}
        <TextField label="Last School Attended" fullWidth value={lastSchoolAttended} onChange={(e) => setLastSchoolAttended(e.target.value)} sx={{ mb: 3 }} placeholder="e.g., Secondary School ABC" required />
        <Typography variant="subtitle2" sx={{ mt: 3, mb: 1, fontWeight: 600, color: '#666' }}>Educational Information</Typography>
        <Autocomplete
          options={[{ _id: 'not_applicable', name: 'Not Applicable', email: '' }, ...schoolOptions]}
          getOptionLabel={(option) => option.name}
          value={selectedSchool}
          inputValue={schoolInput}
          onChange={(_, newValue) => {
            setSelectedSchool(newValue);
            if (!newValue || newValue._id === 'not_applicable') {
              setSchoolSecretId('');
            }
          }}
          onInputChange={(_, value, reason) => {
            setSchoolInput(value);
            if (reason === 'input') {
              setSelectedSchool(null);
            }
          }}
          isOptionEqualToValue={(option, value) => option._id === value._id}
          loading={loadingSchools}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select your school"
              variant="outlined"
              required
              fullWidth
              sx={{ mb: 2 }}
              placeholder="Search available schools"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingSchools ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => (
            <Box component="li" {...props}>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{option.name}</Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>{option.city || option.country || option.email}</Typography>
              </Box>
            </Box>
          )}
        />
        <TextField label="Selected School Email" type="email" fullWidth value={selectedSchool && selectedSchool._id !== 'not_applicable' ? selectedSchool.email : ''} sx={{ mb: 2 }} placeholder="School email will populate after selection" InputProps={{ readOnly: true }} />
        {selectedSchool && selectedSchool._id !== 'not_applicable' && (
          <TextField label="Enter School Secrete ID" fullWidth value={schoolSecretId} onChange={(e) => setSchoolSecretId(e.target.value)} sx={{ mb: 2 }} required />
        )}

        <Typography variant="subtitle2" sx={{ mt: 3, mb: 1, fontWeight: 600, color: '#666' }}>
          Location Information
        </Typography>

        <TextField
          label="Address"
          fullWidth
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          sx={{ mb: 2 }}
          placeholder="Street address"
          required
        />

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 3 }}>
          <TextField
            label="City"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            required
          />
          <TextField
            label="Country"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            required
          />
        </Box>

        <Typography variant="subtitle2" sx={{ mt: 3, mb: 1, fontWeight: 600, color: '#666' }}>
          Parent/Guardian Information
        </Typography>

        <TextField
          label="Parent's Name"
          fullWidth
          value={parentName}
          onChange={(e) => setParentName(e.target.value)}
          sx={{ mb: 2 }}
          required
        />

        <TextField
          label="Parent's Email"
          fullWidth
          type="email"
          value={parentEmail}
          onChange={(e) => setParentEmail(e.target.value)}
          sx={{ mb: 2 }}
          required
        />

        <TextField
          label="Parent's Phone Number"
          fullWidth
          value={parentPhone}
          onChange={(e) => setParentPhone(e.target.value)}
          sx={{ mb: 3 }}
          required
        />

        <Box sx={{ mb: 2.5 }}>
          <AuthLegalNotice creatingAccount />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column-reverse', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 1.5 }}>
          <Button variant="contained" onClick={submit} disabled={loading} size="large" sx={{ minHeight: 48, textTransform: 'none', fontWeight: 700 }}>
            {loading ? 'Creating...' : 'Create account'}
          </Button>
          <Button variant="text" onClick={() => navigate('/high-school/login')}>
            Already have an account?
          </Button>
        </Box>
    </AuthPageFrame>

      <Dialog open={successOpen} onClose={() => {}} disableEscapeKeyDown maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, fontSize: '1.3rem', color: '#2e7d32' }}>
          ✓ Registration Successful
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mt: 2, mb: 2, fontSize: '1rem' }}>
            <strong>Welcome!</strong> Your account has been created successfully.
          </DialogContentText>
          <DialogContentText sx={{ mb: 2 }}>
            Click OK below to proceed to login.
          </DialogContentText>
          <DialogContentText sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>
            You can now sign in to access your High School dashboard.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button variant="contained" onClick={handleSuccessOk} fullWidth>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default HighSchoolSignup;
